import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import { SkeletonCard, SkeletonStats } from '../../ui/skeleton';
import {
  Award, TrendingUp, CheckCircle, AlertTriangle, Download,
  BarChart3, Activity, Target, BookOpen, Shield, Star
} from 'lucide-react';

const ValidacaoCientifica = () => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [cronbachAlpha, setCronbachAlpha] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [relatorioQualidade, setRelatorioQualidade] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    carregarAvaliacoes();
    carregarBenchmarks();
  }, []);

  const carregarAvaliacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/fit-cultural/avaliacoes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        console.error('Erro na resposta:', response.status);
        throw new Error('Erro ao carregar avaliações');
      }

      const data = await response.json();
      console.log('Avaliações carregadas:', data);
      setAvaliacoes(data || []);
    } catch (err) {
      console.error('Erro:', err);
      setError('Não há avaliações criadas ainda. Crie uma avaliação para começar a análise científica.');
      setAvaliacoes([]); // Define array vazio
    }
  };

  const carregarBenchmarks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/fit-cultural/validacao/benchmarks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar benchmarks');

      const data = await response.json();
      setBenchmarks(data);
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const analisarAvaliacao = async (avaliacaoId) => {
    try {
      setLoading(true);
      setError(null);
      setAvaliacaoSelecionada(avaliacaoId);
      
      const token = localStorage.getItem('token');

      // Carregar Cronbach's Alpha
      const alphaResponse = await fetch(
        `${API}/api/fit-cultural/validacao/cronbach-alpha/${avaliacaoId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const alphaData = await alphaResponse.json();
      setCronbachAlpha(alphaData);

      // Carregar Estatísticas
      const statsResponse = await fetch(
        `${API}/api/fit-cultural/validacao/estatisticas-descritivas/${avaliacaoId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const statsData = await statsResponse.json();
      setEstatisticas(statsData);

      // Carregar Relatório de Qualidade
      const relatorioResponse = await fetch(
        `${API}/api/fit-cultural/validacao/relatorio-qualidade/${avaliacaoId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const relatorioData = await relatorioResponse.json();
      setRelatorioQualidade(relatorioData);

    } catch (err) {
      console.error('Erro:', err);
      setError(err.message || 'Erro ao analisar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const getAlphaBadge = (alpha) => {
    if (alpha >= 0.90) {
      return <Badge className="bg-green-100 text-green-800">Excelente (≥ 0.90)</Badge>;
    } else if (alpha >= 0.80) {
      return <Badge className="bg-blue-100 text-blue-800">Bom (0.80-0.89)</Badge>;
    } else if (alpha >= 0.70) {
      return <Badge className="bg-yellow-100 text-yellow-800">Aceitável (0.70-0.79)</Badge>;
    } else if (alpha >= 0.60) {
      return <Badge className="bg-orange-100 text-orange-800">Questionável (0.60-0.69)</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800">Inaceitável (&lt; 0.60)</Badge>;
    }
  };

  const getCertificacaoBadge = (certificacao) => {
    if (certificacao.includes("Excelente")) {
      return (
        <Badge className="bg-green-100 text-green-800 text-base px-4 py-2">
          <CheckCircle className="w-4 h-4 mr-2" />
          {certificacao}
        </Badge>
      );
    } else if (certificacao.includes("Aprovado")) {
      return (
        <Badge className="bg-blue-100 text-blue-800 text-base px-4 py-2">
          <CheckCircle className="w-4 h-4 mr-2" />
          {certificacao}
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-100 text-red-800 text-base px-4 py-2">
          <AlertTriangle className="w-4 h-4 mr-2" />
          {certificacao}
        </Badge>
      );
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Validação Científica
          </h1>
          <p className="text-gray-600 mt-1">
            Análise psicométrica e validação estatística dos instrumentos de Fit Cultural
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Seleção de Avaliação */}
      <Card>
        <CardHeader>
          <CardTitle>Selecionar Avaliação para Análise</CardTitle>
          <CardDescription>
            Escolha uma avaliação para realizar validação científica
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {avaliacoes.map((avaliacao) => (
              <Card
                key={avaliacao.id}
                className={`cursor-pointer hover-lift transition-all ${
                  avaliacaoSelecionada === avaliacao.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'hover:border-gray-300'
                }`}
                onClick={() => analisarAvaliacao(avaliacao.id)}
              >
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{avaliacao.nome}</h3>
                  <p className="text-sm text-gray-600">
                    {avaliacao.perguntas?.length || 0} perguntas
                  </p>
                  {avaliacao.template_cientifico && (
                    <Badge variant="outline" className="mt-2">
                      {avaliacao.template_cientifico}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="space-y-6">
          <SkeletonStats />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Resultados da Análise */}
      {!loading && relatorioQualidade && (
        <div className="space-y-6">
          {/* Score de Qualidade Geral */}
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Star className="w-6 h-6 text-yellow-500" />
                Score de Qualidade Científica
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-6xl font-bold text-blue-600 mb-2">
                    {relatorioQualidade.score_qualidade_geral}
                    <span className="text-3xl text-gray-500">/100</span>
                  </div>
                  {getCertificacaoBadge(relatorioQualidade.certificacao_cientifica)}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">Respondentes</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {relatorioQualidade.numero_respondentes}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cronbach's Alpha */}
          {cronbachAlpha && !cronbachAlpha.erro && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-600" />
                  Análise de Confiabilidade (Cronbach's Alpha)
                </CardTitle>
                <CardDescription>
                  Medida de consistência interna do instrumento
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Cronbach's Alpha</p>
                      <p className="text-4xl font-bold text-blue-600">
                        α = {cronbachAlpha.alpha_geral}
                      </p>
                    </div>
                    <div>
                      {getAlphaBadge(cronbachAlpha.alpha_geral)}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-600">Número de Itens</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {cronbachAlpha.numero_itens}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-600">Respondentes</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {cronbachAlpha.numero_respondentes}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-600">Variância Total</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {cronbachAlpha.variancia_total}
                      </p>
                    </div>
                  </div>

                  {cronbachAlpha.itens_problematicos.length > 0 && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="font-semibold text-orange-800 mb-2">
                        ⚠️ Itens Problemáticos Identificados ({cronbachAlpha.itens_problematicos.length})
                      </p>
                      <ul className="text-sm text-orange-700 space-y-1">
                        {cronbachAlpha.itens_problematicos.map((item, idx) => (
                          <li key={idx}>
                            {item.item}: α aumentaria para {item.alpha_se_deletado} (+{item.aumento})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {cronbachAlpha.recomendacoes.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-semibold text-gray-900">Recomendações:</p>
                      <ul className="space-y-1">
                        {cronbachAlpha.recomendacoes.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Estatísticas Descritivas */}
          {estatisticas && !estatisticas.erro && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Estatísticas Descritivas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Média</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {estatisticas.media_geral.toFixed(2)}%
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Mediana</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {estatisticas.mediana_geral.toFixed(2)}%
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Desvio Padrão</p>
                    <p className="text-2xl font-bold text-green-600">
                      {estatisticas.desvio_padrao_geral.toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Normalidade</p>
                    <p className="text-lg font-bold text-orange-600">
                      {estatisticas.teste_normalidade}
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-semibold text-gray-900 mb-2">Percentis</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">P25</p>
                      <p className="font-bold">{estatisticas.percentil_25.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">P50 (Mediana)</p>
                      <p className="font-bold">{estatisticas.percentil_50.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">P75</p>
                      <p className="font-bold">{estatisticas.percentil_75.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">P90</p>
                      <p className="font-bold">{estatisticas.percentil_90.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Benchmarking com Literatura */}
          {relatorioQualidade.comparacao_literatura && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  Comparação com Literatura Científica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Alpha Obtido</span>
                    <span className="font-bold text-blue-600">
                      {relatorioQualidade.comparacao_literatura.alpha_obtido.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Alpha Mínimo (Literatura)</span>
                    <span className="font-bold text-gray-600">
                      {relatorioQualidade.comparacao_literatura.alpha_minimo_literatura.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Alpha Desejável (Literatura)</span>
                    <span className="font-bold text-gray-600">
                      {relatorioQualidade.comparacao_literatura.alpha_desejavel_literatura.toFixed(3)}
                    </span>
                  </div>
                  <div className={`p-3 rounded font-semibold ${
                    relatorioQualidade.comparacao_literatura.status === "Acima do benchmark"
                      ? 'bg-green-50 text-green-800'
                      : 'bg-orange-50 text-orange-800'
                  }`}>
                    Status: {relatorioQualidade.comparacao_literatura.status}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Problemas e Recomendações */}
          {(relatorioQualidade.problemas.length > 0 || relatorioQualidade.recomendacoes.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatorioQualidade.problemas.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Problemas Identificados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {relatorioQualidade.problemas.map((problema, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {problema}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {relatorioQualidade.recomendacoes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-green-600 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Recomendações
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {relatorioQualidade.recomendacoes.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-green-700">
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ValidacaoCientifica;
